import { z } from 'zod';
import { IsoTime, ServerId } from './basics.js';
import { WazuhAgentConnection } from './wazuh-agent.js';
/**
 * The central server's own health, as an admin sees it (S8, S19; P8.3).
 *
 * Two things fail on the central machine without anybody being told: the daily
 * backup (P8.2), whose failure shows in the journal and `systemctl --failed` and
 * nowhere a person looks, and partition maintenance (section 16), whose failure
 * sends metrics and traffic rows into the default partitions, where they are
 * neither indexed by date nor dropped by the retention rules. `GET /system/health`
 * says both, and the overview shows it to admins.
 */
/** A daily backup missed once, with room for a slow night. */
export const BACKUP_STALE_AFTER_HOURS = 36;
/** Counting stops here: a default partition that has this many is overflowing enough. */
export const DEFAULT_PARTITION_COUNT_CAP = 10_000;
export const BackupHealth = z.object({
    /**
     * `ok` and `stale` from the newest recorded backup's age; `none` when the
     * installer scheduled backups and none has been recorded; `not_scheduled` when
     * this API was not installed by `install.sh central` (a development server, or a
     * database backed up some other way).
     */
    state: z.enum(['ok', 'stale', 'none', 'not_scheduled']),
    newest: z
        .object({
        finishedAt: IsoTime,
        label: z.string().min(1).max(25),
        file: z.string().min(1).max(64),
        bytes: z.number().int().nonnegative(),
    })
        .nullable(),
    staleAfterHours: z.number().int().positive(),
});
export const PartitionOverflow = z.object({
    /** The partitioned table whose default partition this is. */
    table: z.string().min(1).max(64),
    rows: z.number().int().nonnegative(),
    /** True when counting stopped at `DEFAULT_PARTITION_COUNT_CAP`. */
    capped: z.boolean(),
});
/** A Wazuh agent on the central manager that is no server here (P11.8.3). */
export const WazuhAlertsUnknownAgent = z.object({
    name: z.string().min(1).max(128),
    agentId: z.string().regex(/^\d{3,8}$/),
    alerts: z.number().int().nonnegative(),
    lastAt: IsoTime,
});
/**
 * The API's reading of the central Wazuh manager's `alerts.json` (P11.8.3): the
 * last read's outcome, stored with the read position and shown to admins. A
 * reader that has stopped means no server in central mode reports a file change,
 * which is otherwise said nowhere a person looks.
 */
export const WazuhAlertsState = z.object({
    /** `unreadable`: permission denied (not in the `wazuh` group); `missing`: no file. */
    status: z.enum(['ok', 'missing', 'unreadable']),
    /** The last read that found the file. */
    readAt: IsoTime.nullable(),
    checkedAt: IsoTime,
    /** Bytes of the live file not read yet. */
    behindBytes: z.number().int().nonnegative(),
    /** What the last read could not recover, in words (rotated files gone or too large). */
    lost: z.string().max(1000).nullable(),
    unknownAgents: z.array(WazuhAlertsUnknownAgent).max(20),
});
/** A server whose watched paths the API could not send to its Wazuh agent (P11.8.4). */
export const WazuhAgentConfigProblem = z.object({
    serverId: ServerId,
    error: z.string().min(1).max(300),
    since: IsoTime,
});
/**
 * The API writing each server's group `agent.conf` on the central manager
 * (P11.8.4). A server listed under `failing` keeps watching its last good paths,
 * which only this says.
 */
export const WazuhAgentConfigsState = z.object({
    /** When the job last looked; null before its first run. */
    checkedAt: IsoTime.nullable(),
    /** Servers with a group on the manager. */
    servers: z.number().int().nonnegative(),
    /** Written, and their agent has not picked them up yet. */
    waiting: z.number().int().nonnegative(),
    failing: z.array(WazuhAgentConfigProblem).max(20),
});
/**
 * The API reading each server's Wazuh agent from the manager's API (P11.8.5).
 * Stopped answers freeze every server's state as it was, which only this says.
 */
export const WazuhAgentsState = z.object({
    /** The last read that answered; null before the first. */
    readAt: IsoTime.nullable(),
    checkedAt: IsoTime,
    /** Why the last read failed, in words; null when it answered. */
    error: z.string().max(300).nullable(),
    /** Servers by their agent's connection, as last read. */
    connections: z.partialRecord(WazuhAgentConnection, z.number().int().nonnegative()),
});
export const SystemHealth = z.object({
    backups: BackupHealth,
    /**
     * Null where this API reads no Wazuh manager (not installed with one, or the
     * hosted service) or has not read it yet.
     */
    wazuhAlerts: WazuhAlertsState.nullable().default(null),
    /** Null where this API reads no Wazuh manager, as `wazuhAlerts`. */
    wazuhAgentConfigs: WazuhAgentConfigsState.nullable().default(null),
    /** Null where this API has no Wazuh API user, so cannot ask (P11.8.5). */
    wazuhAgents: WazuhAgentsState.nullable().default(null),
    partitions: z.object({
        state: z.enum(['ok', 'overflowing']),
        /** Every partitioned table, with what its default partition holds. */
        defaults: z.array(PartitionOverflow),
    }),
    checkedAt: IsoTime,
});
//# sourceMappingURL=system.js.map